import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  FiUser,
  FiMapPin,
  FiPlus,
  FiTrash2,
  FiX,
  FiSettings,
  FiAlertTriangle,
} from 'react-icons/fi';
import { MdOutlineLocationOff } from 'react-icons/md';
import { Header } from '../layout/Header';
import { BottomNav } from '../layout/BottomNav';
import {
  useGetPreferredLocations,
  useAddPreferredLocation,
  useDeletePreferredLocation,
} from '../../api/profile';
import { LocationSearchInput } from '../common/LocationSearchInput';
import type { LocationBody } from '../../services/locationService';

// ─── Types ─────────────────────────────────────────────────────────────────

interface PreferredLocation {
  id: number;
  city_location: string;
  state_location: string;
}

// ─── Delete Confirmation Dialog ─────────────────────────────────────────────

interface DeleteDialogProps {
  location: PreferredLocation;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

function DeleteDialog({ location, onConfirm, onCancel, isDeleting }: DeleteDialogProps) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-sm bg-zinc-900 border border-white/8 rounded-3xl p-6 shadow-2xl"
        style={{ animation: 'fadeInScale 0.18s ease-out' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center shrink-0">
            <FiAlertTriangle size={18} className="text-red-400" />
          </div>
          <h2 className="text-white font-bold text-[17px]">Remove Location</h2>
        </div>

        <p className="text-zinc-400 text-[14px] leading-relaxed mb-6">
          Are you sure you want to remove{' '}
          <span className="text-white font-semibold">
            {location.city_location}, {location.state_location}
          </span>{' '}
          from your preferred locations?
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 h-12 rounded-2xl border border-white/10 bg-white/5 text-white text-[14px] font-semibold hover:bg-white/8 transition-all disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 h-12 rounded-2xl bg-red-500/80 hover:bg-red-500 text-white text-[14px] font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
          >
            {isDeleting ? (
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <FiTrash2 size={15} />
                Remove
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Location Modal ──────────────────────────────────────────────────────

interface AddLocationModalProps {
  onClose: () => void;
  onAdd: (loc: LocationBody) => void;
  isAdding: boolean;
}

function AddLocationModal({ onClose, onAdd, isAdding }: AddLocationModalProps) {
  const [selectedLocation, setSelectedLocation] = useState<LocationBody | null>(null);

  const handleAdd = () => {
    if (!selectedLocation) return;
    onAdd(selectedLocation);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full md:max-w-lg bg-zinc-900 border border-white/8 md:rounded-3xl rounded-t-3xl p-6 shadow-2xl"
        style={{ animation: 'slideUp 0.22s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <FiMapPin size={16} className="text-emerald-400" />
            </div>
            <h2 className="text-white font-bold text-[17px]">Add Location</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer"
          >
            <FiX size={15} className="text-zinc-400" />
          </button>
        </div>

        <p className="text-zinc-500 text-[13px] mb-5 leading-relaxed">
          Pick a city you want to discover events and connect with artists from.
        </p>

        {/* Location search */}
        <div className="mb-6">
          <LocationSearchInput
            onLocationSelected={setSelectedLocation}
            placeholder="Search for a city or state…"
            lax
          />
        </div>

        {/* Selected preview */}
        {selectedLocation && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-emerald-500/8 border border-emerald-500/20 mb-5">
            <FiMapPin size={15} className="text-emerald-400 shrink-0" />
            <p className="text-white text-[13px] font-medium truncate">
              {selectedLocation.city}
              {selectedLocation.state ? `, ${selectedLocation.state}` : ''}
              {selectedLocation.country ? `, ${selectedLocation.country}` : ''}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-12 rounded-2xl border border-white/10 bg-white/5 text-white text-[14px] font-semibold hover:bg-white/8 transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedLocation || isAdding}
            className="flex-1 h-12 rounded-2xl bg-white text-black text-[14px] font-bold hover:bg-zinc-200 transition-all disabled:opacity-40 flex items-center justify-center gap-2 cursor-pointer"
          >
            {isAdding ? (
              <span className="inline-block w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <FiPlus size={16} />
                Add Location
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Location Card ───────────────────────────────────────────────────────────

interface LocationCardProps {
  location: PreferredLocation;
  onDelete: (loc: PreferredLocation) => void;
}

function LocationCard({ location, onDelete }: LocationCardProps) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 bg-zinc-900/50 border border-white/5 rounded-2xl hover:border-white/10 transition-all duration-200 group">
      {/* Icon */}
      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
        <FiMapPin size={17} className="text-emerald-400" />
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-[15px] font-semibold truncate">{location.city_location}</p>
        <p className="text-zinc-500 text-[12px] mt-0.5">{location.state_location}</p>
      </div>

      {/* Delete */}
      <button
        onClick={() => onDelete(location)}
        className="w-8 h-8 rounded-xl flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200 cursor-pointer md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 opacity-100"
        aria-label={`Remove ${location.city_location}`}
      >
        <FiTrash2 size={15} />
      </button>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-emerald-500/8 border border-emerald-500/15 flex items-center justify-center mb-5">
        <MdOutlineLocationOff size={30} className="text-emerald-500/60" />
      </div>
      <h3 className="text-white font-bold text-[18px] mb-2">No preferred locations</h3>
      <p className="text-zinc-500 text-[14px] leading-relaxed max-w-xs mb-6">
        Add locations where you want to discover events and connect with artists.
      </p>
      <button
        onClick={onAdd}
        className="h-11 px-6 rounded-2xl bg-white text-black text-[14px] font-bold hover:bg-zinc-200 transition-all flex items-center gap-2 cursor-pointer"
      >
        <FiPlus size={16} />
        Add Your First Location
      </button>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export const PreferredLocationsPage: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<PreferredLocation | null>(null);

  const { data: locationsData, isLoading } = useGetPreferredLocations();
  const { mutate: addLocation, isPending: isAdding } = useAddPreferredLocation();
  const { mutate: deleteLocation, isPending: isDeleting } = useDeletePreferredLocation();

  // Normalise the API response – the backend returns { preferred_locations: [...], count: N }
  const locations: PreferredLocation[] = locationsData?.preferred_locations ?? [];

  const handleAdd = (loc: LocationBody) => {
    if (!loc.city) return;
    addLocation(
      { city: loc.city, state: loc.state, country: loc.country },
      {
        onSuccess: () => {
          setShowAddModal(false);
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!locationToDelete) return;
    deleteLocation(
      { city: locationToDelete.city_location, state: locationToDelete.state_location },
      {
        onSuccess: () => {
          setLocationToDelete(null);
        },
      },
    );
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 mb-10 px-1 text-zinc-500 text-[12px] font-medium tracking-wide flex-wrap">
          <Link
            to="/profile"
            className="flex items-center gap-1.5 hover:text-white transition-all group/crumb"
          >
            <FiUser
              size={13}
              className="text-blue-500/80 group-hover/crumb:text-blue-400 transition-colors"
            />
            <span>Profile</span>
          </Link>
          <span className="text-zinc-800 font-light">/</span>
          <Link
            to="/profile/settings"
            className="flex items-center gap-1.5 hover:text-white transition-all group/crumb"
          >
            <FiSettings
              size={13}
              className="text-blue-500/80 group-hover/crumb:text-blue-400 transition-colors"
            />
            <span>Settings</span>
          </Link>
          <span className="text-zinc-800 font-light">/</span>
          <span className="text-zinc-200">Preferred Locations</span>
        </nav>

        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <FiMapPin size={20} className="text-emerald-400" />
              </div>
              <h1 className="text-white font-bold text-[26px] md:text-[28px]">
                Preferred Locations
              </h1>
            </div>
            <p className="text-zinc-500 text-[14px] ml-1">
              Choose locations where you want to discover events and connect with artists.
            </p>
          </div>

          {/* Desktop add button */}
          {!isLoading && locations.length > 0 && (
            <button
              onClick={() => setShowAddModal(true)}
              className="hidden md:flex items-center gap-2 h-10 px-5 rounded-2xl bg-white text-black text-[13px] font-bold hover:bg-zinc-200 transition-all shrink-0 cursor-pointer"
            >
              <FiPlus size={15} />
              Add Location
            </button>
          )}
        </div>

        {/* Location count badge */}
        {!isLoading && locations.length > 0 && (
          <div className="flex items-center gap-2 mb-5 px-1">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <FiMapPin size={12} className="text-emerald-400" />
              <span className="text-emerald-400 text-[12px] font-semibold">
                {locations.length} Location{locations.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-zinc-900/20 border border-white/5 rounded-3xl overflow-hidden">
          {isLoading ? (
            // Skeleton
            <div className="p-5 flex flex-col gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[72px] rounded-2xl bg-zinc-800/50 animate-pulse" />
              ))}
            </div>
          ) : locations.length === 0 ? (
            <EmptyState onAdd={() => setShowAddModal(true)} />
          ) : (
            <div className="p-4 flex flex-col gap-3">
              {locations.map((loc) => (
                <LocationCard
                  key={loc.id}
                  location={loc}
                  onDelete={(l) => setLocationToDelete(l)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Mobile add button */}
        {!isLoading && locations.length > 0 && (
          <div className="mt-5 md:hidden">
            <button
              onClick={() => setShowAddModal(true)}
              className="w-full h-14 rounded-2xl bg-white text-black text-[15px] font-bold hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <FiPlus size={18} />
              Add Location
            </button>
          </div>
        )}
      </main>

      {/* Add location modal */}
      {showAddModal && (
        <AddLocationModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
          isAdding={isAdding}
        />
      )}

      {/* Delete confirmation */}
      {locationToDelete && (
        <DeleteDialog
          location={locationToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setLocationToDelete(null)}
          isDeleting={isDeleting}
        />
      )}

      <BottomNav />

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
